import { useState } from "react";
import { useAuth, type RegisterPayload } from "@/store/AuthStore";

const inputClass =
  "w-full bg-background-100/70 border border-background-300/50 rounded-md px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/60 focus:ring-2 focus:ring-accent-500/25";
const labelClass = "block text-xs font-semibold text-foreground-600 mb-1.5";

interface RegisterFormProps {
  onSwitch: () => void;
}

const initial = {
  names: "",
  surnames: "",
  cedula: "",
  phone: "",
  email: "",
  password: "",
  address: "",
  city: "",
  refName: "",
  refRelationship: "",
  refPhone: "",
  refCity: "",
};

const RegisterForm = ({ onSwitch }: RegisterFormProps) => {
  const { signUp } = useAuth();
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof initial) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setNotice("");
    if (!form.names.trim() || !form.surnames.trim()) {
      setError("Escribe tus nombres y apellidos.");
      return;
    }
    if (!form.cedula.trim()) {
      setError("La cédula es obligatoria.");
      return;
    }
    if (!form.email.trim() || form.password.length < 6) {
      setError("Ingresa un correo válido y una contraseña de al menos 6 caracteres.");
      return;
    }
    if (!form.refName.trim() || !form.refPhone.trim()) {
      setError("La referencia personal es obligatoria (nombre y celular).");
      return;
    }

    const payload: RegisterPayload = {
      names: form.names.trim(),
      surnames: form.surnames.trim(),
      cedula: form.cedula.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      // Las condiciones comerciales (margen y cupo) las asigna el administrador
      // desde la ficha máster una vez aprueba la cuenta.
      margin: 25,
      consignmentLimit: 0,
      refName: form.refName.trim(),
      refRelationship: form.refRelationship.trim(),
      refPhone: form.refPhone.trim(),
      refCity: form.refCity.trim(),
    };

    setBusy(true);
    try {
      const { needsConfirmation } = await signUp(form.email.trim(), form.password, payload);
      if (needsConfirmation) {
        setNotice(
          "Cuenta creada. Revisa tu correo para confirmarla, inicia sesión y espera la aprobación del administrador."
        );
        setForm(initial);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo crear la cuenta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="rounded-md bg-accent-500/10 border border-accent-500/25 px-3 py-2.5">
        <p className="text-[11px] text-foreground-700 flex items-start gap-2">
          <i className="ri-information-line text-accent-300 mt-0.5" />
          <span>
            La <strong>primera cuenta</strong> creada será el Administrador (Juanka). Las siguientes
            serán cuentas de asesor y quedarán <strong>pendientes de aprobación</strong>: podrás
            entrar a tu portal una vez el administrador apruebe tu registro. El <strong>margen
            comercial</strong> y el <strong>cupo de consignación</strong> los define el administrador
            al aprobar tu cuenta.
          </span>
        </p>
      </div>

      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-accent-300 mb-2">
          Datos personales
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Nombres</label>
            <input value={form.names} onChange={set("names")} placeholder="Pepito" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Apellidos</label>
            <input value={form.surnames} onChange={set("surnames")} placeholder="Jiménez" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Cédula</label>
            <input value={form.cedula} onChange={set("cedula")} placeholder="1020456789" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Celular / WhatsApp</label>
            <input value={form.phone} onChange={set("phone")} placeholder="300 111 2233" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Dirección / Barrio</label>
            <input value={form.address} onChange={set("address")} placeholder="Calle 45 #12-30" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Ciudad</label>
            <input value={form.city} onChange={set("city")} placeholder="Bogotá" className={inputClass} />
          </div>
        </div>
      </div>

      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-accent-300 mb-2">
          Acceso a la plataforma
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Rol de la cuenta</label>
            <div className="w-full flex items-center gap-2 bg-background-200/50 border border-background-300/50 rounded-md px-3 py-2.5 text-sm text-foreground-700">
              <i className="ri-lock-2-line text-accent-300" />
              <span>Asesor / Distribuidor</span>
              <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-secondary-500/20 text-secondary-200">
                Fijo
              </span>
            </div>
            <p className="text-[10px] text-foreground-500 mt-1">
              El registro público solo crea cuentas de asesor. El Administrador se asigna aparte.
            </p>
          </div>
          <div />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className={labelClass}>Correo electrónico</label>
            <input type="email" value={form.email} onChange={set("email")} placeholder="tu@correo.com" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Contraseña (mín. 6)</label>
            <input type="password" value={form.password} onChange={set("password")} placeholder="••••••••" className={inputClass} />
          </div>
        </div>
      </div>

      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-accent-300 mb-2">
          Garantía · Referencia personal
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Nombre completo</label>
            <input value={form.refName} onChange={set("refName")} placeholder="María Jiménez" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Parentesco</label>
            <input value={form.refRelationship} onChange={set("refRelationship")} placeholder="Madre" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Celular</label>
            <input value={form.refPhone} onChange={set("refPhone")} placeholder="310 444 5566" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Ciudad</label>
            <input value={form.refCity} onChange={set("refCity")} placeholder="Bogotá" className={inputClass} />
          </div>
        </div>
      </div>

      {error && (
        <p className="text-xs text-primary-200 bg-primary-500/15 border border-primary-400/30 rounded-md px-3 py-2 flex items-center gap-2 animate-fade-up">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
      {notice && (
        <p className="text-xs text-accent-200 bg-accent-500/15 border border-accent-500/30 rounded-md px-3 py-2 flex items-center gap-2 animate-fade-up">
          <i className="ri-mail-check-line" /> {notice}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          disabled={busy}
          className="flex-1 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
        >
          {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-user-add-line" />}
          {busy ? "Creando cuenta..." : "Registrarme como asesor"}
        </button>
        <button
          type="button"
          onClick={onSwitch}
          className="px-4 py-2.5 rounded-md border border-accent-500/30 text-foreground-700 hover:text-foreground-950 hover:border-accent-500/50 text-sm font-semibold whitespace-nowrap cursor-pointer"
        >
          Ya tengo cuenta
        </button>
      </div>
    </form>
  );
};

export default RegisterForm;